import { ConnectedPosition } from '@angular/cdk/overlay';
import { ChangeDetectionStrategy, Component, computed, inject } from '@angular/core';

import { ImportSiteAmendmentsDialogComponent } from '@widgets/import-site-amendments-dialog/import-site-amendments-dialog.component';

import { SiteWorkflowDirective } from '@features/feature-workflow/directives/site-workflow.directive';

import { injectProtocolVersions } from '@models/patient-protocol/api/patient-protocol.api';

import {
  AuxButtonGroup,
  ButtonGroupComponent,
} from '@shared/components/button-group/button-group.component';
import { MessagesConstants } from '@shared/constants/messages.constants';
import { MultiTooltipDirective } from '@shared/directives/multi-tooltip.directive';
import { WorkflowStep } from '@shared/services/gql.service';
import { injectFlags } from '@shared/services/launch-darkly.service';
import { OverlayService } from '@shared/services/overlay.service';
import { WorkflowQuery } from '@shared/store/workflow/workflow.query';
import { Option } from '@shared/types/components.type';

import { ImportSiteCostDataModelComponent } from '../import-site-cost-data-model/import-site-cost-data-model.component';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [ButtonGroupComponent, MultiTooltipDirective],
  providers: [SiteWorkflowDirective],
  selector: 'aux-patient-protocol-actions',
  template: `
    <aux-button-group
      variant="secondary"
      label="Add"
      [options]="editOptions()"
      [position]="addButtonGroupOverlayPosition"
      [disabled]="siteWorkflowDirective.disabledUpdateSiteCostUI()"
      [auxMultiTooltip]="siteWorkflowDirective.updateSiteCostTooltips()"
    />
  `,
})
export class PatientProtocolActionsComponent {
  overlayService = inject(OverlayService);
  siteWorkflowDirective = inject(SiteWorkflowDirective);
  workflowQuery = inject(WorkflowQuery);

  flags = injectFlags({ visitCosts: 'visit_costs' });
  protocolVerions = injectProtocolVersions();

  isPatientsFinalized = this.workflowQuery.getLockStatusByWorkflowStepType(
    WorkflowStep.WF_STEP_MONTH_CLOSE_LOCK_PATIENT_TRACKER
  );

  patientTrackerLockedTooltip = computed(() =>
    this.isPatientsFinalized() ? MessagesConstants.PATIENT_TRACKER_CLOSED : ''
  );
  protocolVersionOptions = computed(() => {
    return (this.protocolVerions.data() || []).map((version) => ({
      label: version.name,
      value: version.id,
    }));
  });
  editOptions = computed<AuxButtonGroup[]>(() => {
    return [
      {
        disabled:
          this.siteWorkflowDirective.disabledUpdateSiteCostUI() || this.isPatientsFinalized(),
        disabledTooltips: [
          this.patientTrackerLockedTooltip(),
          this.siteWorkflowDirective.siteCostActionTooltip(),
        ].filter(Boolean),
        name: 'Import Site Cost Data',
        onClick: () => {
          this.overlayService.openPopup<
            {
              protocolVersionOptions: Option[];
            },
            unknown,
            ImportSiteCostDataModelComponent
          >({
            content: ImportSiteCostDataModelComponent,
            data: {
              protocolVersionOptions: this.protocolVersionOptions(),
            },
            settings: {
              header: 'Upload Site Cost Data',
              panelClasses: ['min-w-[569px]'],
              primaryButton: {
                action: (instance) => instance?.onUpload(),
                disabled: this.isPatientsFinalized(),
                label: 'Upload',
                tooltip: this.patientTrackerLockedTooltip(),
              },
            },
          });
        },
      },
      {
        disabled:
          this.siteWorkflowDirective.disabledUpdateSiteCostUI() || this.isPatientsFinalized(),
        disabledTooltips: [
          this.patientTrackerLockedTooltip(),
          this.siteWorkflowDirective.addSiteActionTooltip(),
        ].filter(Boolean),
        name: 'Upload Bulk Amendments',
        onClick: () => {
          this.overlayService.openPopup<
            {
              protocolVersionOptions: Option<string>[];
            },
            unknown,
            ImportSiteAmendmentsDialogComponent
          >({
            content: ImportSiteAmendmentsDialogComponent,
            data: {
              protocolVersionOptions: this.protocolVersionOptions(),
            },
            settings: {
              header: 'Upload Bulk Amendments',
              panelClasses: ['min-w-[569px]'],
              primaryButton: {
                action: (instance) => instance?.onUpload(),
                label: 'Upload',
              },
            },
          });
        },
      },
    ];
  });

  addButtonGroupOverlayPosition: ConnectedPosition[] = [
    {
      originX: 'end',
      originY: 'bottom',
      overlayX: 'end',
      overlayY: 'top',
    },
  ];
}
